<template>
  <ui-modal-layout :title="tr('Estimated savings')" :icon="PiggyBankIcon" stacked>
    <template #header>
      <span v-if="estimate" class="text-2xl font-bold tracking-tight text-primary tabular-nums">
        {{ formatPrice(estimate.total_amount, currency) }}
      </span>
    </template>

    <p v-if="!estimate" class="m-0 py-10 text-center text-muted-foreground">
      {{ tr('No data available.') }}
    </p>

    <template v-else>
      <p class="m-0 text-muted-foreground">
        {{ tr('This estimate shows the savings generated by using :app, including avoided ad overspend and the manual work time saved through automation.', { ':app': 'Infinite∞Ad' }) }}
      </p>

      <section class="glass-tile flex flex-col gap-3 rounded-xl p-4">
        <div class="flex items-baseline justify-between gap-3 font-semibold">
          <span>
            {{ tr('Saved ad spend') }}
            <span class="text-xs font-normal text-muted-foreground">({{ tr('avoided overspend') }})</span>
          </span>
          <span class="tabular-nums">{{ formatPrice(estimate.stopped_ads.total_amount, currency) }}</span>
        </div>
        <div
          v-for="row in adSpendRows"
          :key="row.key"
          class="flex items-baseline justify-between gap-3 border-t border-foreground/8 pt-3"
        >
          <span class="flex flex-col">
            {{ row.label }}
            <span class="text-xs text-muted-foreground">{{ tr(':count ad(s) stopped', { ':count': row.count }) }}</span>
          </span>
          <span class="tabular-nums">{{ formatPrice(row.amount, currency) }}</span>
        </div>
      </section>

      <section class="glass-tile flex flex-col gap-3 rounded-xl p-4">
        <div class="flex items-baseline justify-between gap-3 font-semibold">
          <span>{{ tr('Saved working time') }}</span>
          <span class="text-right tabular-nums">
            {{ formatHours(estimate.manual_labor.total_minutes / 60) }}
            <span class="block text-xs font-normal text-muted-foreground">≈ {{ formatPrice(estimate.manual_labor.total_amount, currency) }}</span>
          </span>
        </div>
        <div
          v-for="row in laborBreakdown"
          :key="row.key"
          class="flex items-baseline justify-between gap-3 border-t border-foreground/8 pt-3"
        >
          <span class="flex flex-col">
            {{ tr(row.label) }}
            <span class="text-xs text-muted-foreground">{{ tr(':count × :minutes min', { ':count': row.count, ':minutes': row.minutesEach }) }}</span>
          </span>
          <span class="tabular-nums">{{ formatMinutes(row.totalMinutes) }}</span>
        </div>
      </section>
    </template>

    <p class="m-0 text-xs text-muted-foreground">
      <template v-if="hasCurrencyConversion">
        {{ tr('Calculated with an average :days-day manual reaction time and an average :rate/h labor rate (≈ :converted/h).', {
          ':days': savingsSettings.reaction_days,
          ':rate': formatPrice(savingsSettings.hourly_rate, settingsCurrency),
          ':converted': formatPrice(estimate.hourly_rate_converted, currency),
        }) }}
      </template>
      <template v-else>
        {{ tr('Calculated with an average :days-day manual reaction time and an average :rate/h labor rate.', {
          ':days': savingsSettings.reaction_days,
          ':rate': formatPrice(savingsSettings.hourly_rate, settingsCurrency),
        }) }}
      </template>
    </p>

    <template #footer>
      <ui-button variant="outline" class="w-full" @click="() => onAdjustAssumptions()">
        <sliders-horizontal-icon />
        {{ tr('Adjust assumptions') }}
      </ui-button>
    </template>
  </ui-modal-layout>
</template>

<script>
import { PiggyBank, SlidersHorizontal } from '@lucide/vue';
import { markRaw } from 'vue';

import { ESTIMATED_SAVINGS_ACTIONS } from '@/consts.js';
import { formatPrice } from '@/utils/number';

/** Content of the dashboard "Savings" details modal. */
export default {
  name: 'EstimatedSavingsDialog',
  components: {
    SlidersHorizontalIcon: SlidersHorizontal,
  },
  props: {
    estimate: {
      type: Object,
      default: null,
    },
    currency: {
      type: String,
      default: null,
    },
    onAdjustAssumptions: {
      type: Function,
      default: () => null,
    },
  },
  data: () => ({
    PiggyBankIcon: markRaw(PiggyBank),
  }),
  computed: {
    savingsSettings() {
      return this.$root.loggedInUser.dashboard_settings.savings_estimate || {};
    },
    settingsCurrency() {
      return this.savingsSettings.hourly_rate_currency || this.currency;
    },
    hasCurrencyConversion() {
      return this.settingsCurrency
        && this.currency
        && this.settingsCurrency.toUpperCase() !== this.currency.toUpperCase();
    },
    adSpendRows() {
      const stoppedAds = this.estimate.stopped_ads;

      return [
        { key: 'condition', label: this.tr('From underperforming ads'), ...stoppedAds.condition_based },
        { key: 'expired', label: this.tr('From expired content'), ...stoppedAds.expired_content },
      ];
    },
    laborBreakdown() {
      if (!this.estimate || !this.estimate.manual_labor) {
        return [];
      }

      const minutes = this.savingsSettings.minutes_per_action || {};
      const breakdown = this.estimate.manual_labor.breakdown || {};

      return ESTIMATED_SAVINGS_ACTIONS.map(action => ({
        key: action.key,
        label: action.label,
        count: breakdown[action.key] ? breakdown[action.key].count : 0,
        minutesEach: minutes[action.key] || 0,
        totalMinutes: breakdown[action.key] ? breakdown[action.key].minutes : 0,
      }));
    },
  },
  methods: {
    formatPrice,
    formatHours(hours) {
      const rounded = hours ? Math.round(hours * 10) / 10 : 0;
      return this.tr(':num h', { ':num': rounded });
    },
    formatMinutes(minutes) {
      return this.tr(':num min', { ':num': minutes || 0 });
    },
  },
};
</script>
